import { types } from 'mobx-state-tree';
import { BrowseNavItemType } from '../types/navigation';
function isBrowseNavSection(node) {
  return node.type === BrowseNavItemType.SECTION;
}

/**
 * Organized section for rendering - groups items with section metadata
 * Used to structure navigation items into logical groups with dividers
 */

/**
 * Navigation configuration that handles all aspects of navigation behavior
 * Each header variant (logged-out, personal-plan, organization) provides one of these
 */

/**
 * MST store that manages navigation structure with a lookup map for efficient access
 * All loading states are tracked in the lookup map, no tree traversals needed
 */
export const HeaderMenuStore = types.model('HeaderMenuStore', {
  navigationStructure: types.maybeNull(types.frozen()),
  isInitialized: types.optional(types.boolean, false),
  isEagerLoadingComplete: types.optional(types.boolean, false),
  // Lookup map for node states - maps node ID to loading state
  nodeStates: types.map(types.model({
    isLoading: types.optional(types.boolean, false)
  })),
  // Navigation state - tracks active items at each level
  navigationState: types.map(types.frozen())
}).volatile(() => ({
  navigationConfig: null,
  // Track which nodes we've attempted to fetch to prevent duplicate attempts
  fetchAttempts: new Set()
})).views(self => ({
  /**
   * Check if the store is in global loading state
   * Returns true if not initialized or eager loading is not complete
   */
  get isGlobalLoading() {
    return !self.isInitialized || !self.isEagerLoadingComplete;
  },
  /**
   * Get loading state for a specific node by ID
   * No tree traversal needed - just lookup in the map
   */
  isNodeLoading(nodeId) {
    var _nodeState$isLoading;
    const nodeState = self.nodeStates.get(nodeId);
    return (_nodeState$isLoading = nodeState === null || nodeState === void 0 ? void 0 : nodeState.isLoading) !== null && _nodeState$isLoading !== void 0 ? _nodeState$isLoading : false;
  },
  /**
   * Get active item at a specific level
   * Returns the current version from navigation structure, not the stored reference
   */
  getActiveItemAtLevel(level) {
    const storedItem = self.navigationState.get(level.toString());
    if (!storedItem || !self.navigationStructure) {
      return storedItem;
    }

    // Find the current version of this item in the navigation structure
    return this.findItemInStructure(self.navigationStructure, storedItem.id) || storedItem;
  },
  /**
   * Recursively find an item by ID in the navigation structure
   */
  findItemInStructure(node, targetId) {
    if (node.type !== BrowseNavItemType.SECTION && node.id === targetId) {
      return node;
    }
    if (node.children) {
      for (const child of node.children) {
        const found = this.findItemInStructure(child, targetId);
        if (found) return found;
      }
    }
    return undefined;
  },
  /**
   * Recursively find any node (item or section) by ID in the navigation structure
   */
  findNodeInStructure(node, targetId) {
    if (node.id === targetId) {
      return node;
    }
    if (node.children) {
      for (const child of node.children) {
        const found = this.findNodeInStructure(child, targetId);
        if (found) return found;
      }
    }
    return undefined;
  },
  /**
   * Organize navigation items into sections for rendering
   * Groups direct items together and separates sections with borders
   */
  organizeNavigationItems(children) {
    const sections = [];
    let hasProcessedFirstSection = false;
    const directItems = [];

    // Guard against non-array children
    if (!Array.isArray(children)) {
      return sections;
    }
    children.forEach(child => {
      if (child.type === BrowseNavItemType.SECTION) {
        var _section$children;
        // If we have accumulated direct items, add them as a section first
        if (directItems.length > 0) {
          sections.push({
            items: [...directItems],
            needsBorder: hasProcessedFirstSection
          });
          directItems.length = 0; // Clear the array
          hasProcessedFirstSection = true;
        }
        const section = child;
        const sectionItems = ((_section$children = section.children) !== null && _section$children !== void 0 ? _section$children : []).filter(item => item.type !== BrowseNavItemType.SECTION);
        sections.push({
          items: sectionItems,
          section: section,
          needsBorder: hasProcessedFirstSection
        });
        hasProcessedFirstSection = true;
      } else {
        // Collect direct items to group them together
        directItems.push(child);
      }
    });

    // Add any remaining direct items as a final section
    if (directItems.length > 0) {
      sections.push({
        items: directItems,
        needsBorder: hasProcessedFirstSection
      });
    }
    return sections;
  },
  /**
   * Get all navigation levels that should be rendered
   * Computes the visible navigation hierarchy based on active items
   */
  get visibleNavigationLevels() {
    var _self$navigationStruc;
    const levels = [];
    if (!self.navigationStructure) return levels;

    // Always show root level
    let currentLevel = 0;
    let currentItems = (_self$navigationStruc = self.navigationStructure.children) !== null && _self$navigationStruc !== void 0 ? _self$navigationStruc : [];
    let parentItem;
    while (true) {
      const activeItem = this.getActiveItemAtLevel(currentLevel);
      const hasNextLevel = activeItem && (activeItem.children || this.isNodeLoading(activeItem.id) || activeItem.fetchChildren);

      // Organize items into sections at store level
      const organizedSections = this.organizeNavigationItems(currentItems);
      levels.push({
        organizedSections,
        level: currentLevel,
        parentItem,
        isLastLevel: !hasNextLevel
      });

      // Stop if no next level
      if (!hasNextLevel || !activeItem) break;

      // Prepare next level
      const isLoading = this.isNodeLoading(activeItem.id);
      currentItems = isLoading ? [] : activeItem.children || [];
      parentItem = activeItem;
      currentLevel++;
    }
    return levels;
  }
})).actions(self => ({
  /**
   * Set the navigation configuration
   */
  setConfig(navigationConfig) {
    self.navigationConfig = navigationConfig;
  },
  /**
   * Mark eager loading as complete - separate action for use after async operations
   */
  setEagerLoadingComplete() {
    self.isEagerLoadingComplete = true;
  },
  /**
   * Initialize the store with the navigation structure
   * Fetches any items marked with eagerLoad: true
   */
  async initialize() {
    if (!self.navigationConfig) {
      throw new Error('Navigation config must be set before initializing');
    }
    const initialStructure = self.navigationConfig.getInitialStructure();
    self.navigationStructure = initialStructure;
    self.isInitialized = true;
    try {
      // Eagerly fetch any items marked with eagerLoad: true
      await this.fetchEagerLoadItems(initialStructure);
    } finally {
      // Mark eager loading as complete even if there were errors
      // (must be in action context since it's after await of async operation)
      this.setEagerLoadingComplete();
    }
  },
  /**
   * Initialize with eager loading - fetches all fetchChildren functions immediately
   * This is useful for Storybook and scenarios where we want to pre-populate all data
   * @deprecated Use initialize() with eagerLoad property on individual items instead
   */
  async initializeWithEagerLoading() {
    if (!self.navigationConfig) {
      throw new Error('Navigation config must be set before initializing');
    }
    const initialStructure = self.navigationConfig.getInitialStructure();
    self.navigationStructure = initialStructure;
    self.isInitialized = true;
    try {
      // Recursively find and fetch all items with fetchChildren
      await this.eagerlyFetchAllChildren(initialStructure);
    } finally {
      // Mark eager loading as complete even if there were errors
      // (must be in action context)
      this.setEagerLoadingComplete();
    }
  },
  /**
   * Fetch only items marked with eagerLoad: true
   * This allows selective pre-loading of specific sections
   */
  async fetchEagerLoadItems(node) {
    const fetchPromises = [];

    // If this node has fetchChildren AND eagerLoad is true, fetch it
    if (node.fetchChildren && node.eagerLoad === true) {
      const fetchPromise = this.fetchNodeChildren(node.id, node.fetchChildren);
      fetchPromises.push(fetchPromise);
    }

    // Recursively process existing children
    if (node.children) {
      for (const child of node.children) {
        const childPromise = this.fetchEagerLoadItems(child);
        fetchPromises.push(childPromise);
      }
    }

    // Wait for all fetches at this level to complete
    await Promise.all(fetchPromises);

    // After fetching eager items, check if any newly loaded children also have eagerLoad
    if (node.fetchChildren && node.eagerLoad === true && self.navigationStructure) {
      const updatedNode = self.findNodeInStructure(self.navigationStructure, node.id);
      if (updatedNode !== null && updatedNode !== void 0 && updatedNode.children) {
        // Recursively fetch eager children of newly loaded nodes
        const newChildPromises = updatedNode.children.map(child => this.fetchEagerLoadItems(child));
        await Promise.all(newChildPromises);
      }
    }
  },
  /**
   * Recursively fetch all children that have fetchChildren functions
   * This pre-populates the entire navigation tree (for backward compatibility)
   */
  async eagerlyFetchAllChildren(node) {
    const fetchPromises = [];

    // If this node has fetchChildren, fetch it (fetchNodeChildren handles duplicate prevention)
    if (node.fetchChildren) {
      const fetchPromise = this.fetchNodeChildren(node.id, node.fetchChildren);
      fetchPromises.push(fetchPromise);
    }

    // Recursively process existing children
    if (node.children) {
      for (const child of node.children) {
        const childPromise = this.eagerlyFetchAllChildren(child);
        fetchPromises.push(childPromise);
      }
    }

    // Wait for all fetches at this level to complete
    await Promise.all(fetchPromises);

    // After fetching, we need to check the updated structure for newly loaded children
    if (node.fetchChildren && self.navigationStructure) {
      const updatedNode = self.findNodeInStructure(self.navigationStructure, node.id);
      if (updatedNode !== null && updatedNode !== void 0 && updatedNode.children) {
        // Recursively fetch children of newly loaded nodes
        const newChildPromises = updatedNode.children.map(child => this.eagerlyFetchAllChildren(child));
        await Promise.all(newChildPromises);
      }
    }
  },
  /**
   * Set loading state for a specific node
   */
  setNodeLoading(nodeId, isLoading) {
    if (isLoading) {
      // Create or update the node state
      self.nodeStates.set(nodeId, {
        isLoading: true
      });
    } else {
      // Remove from map when not loading to keep it clean
      self.nodeStates.delete(nodeId);
    }
  },
  /**
   * Handle async fetch for a node - manages loading state automatically
   * Includes built-in checks to prevent duplicate fetches
   */
  async fetchNodeChildren(nodeId, fetchFn) {
    // Check if we've already attempted to fetch this node
    if (self.fetchAttempts.has(nodeId)) {
      return;
    }

    // Check if already loading - prevent duplicate fetches
    if (self.isNodeLoading(nodeId)) {
      return;
    }

    // Check if node has already been fetched (has children array, even if empty)
    // We need to check the current state before we fetch
    const targetNode = self.navigationStructure ? self.findNodeInStructure(self.navigationStructure, nodeId) : undefined;

    // If the node has a children array (even empty), it means we've already fetched
    // We distinguish between undefined (never fetched) and [] (fetched but empty)
    if ((targetNode === null || targetNode === void 0 ? void 0 : targetNode.children) !== undefined) {
      return;
    }

    // Mark this node as attempted before we start the fetch
    self.fetchAttempts.add(nodeId);
    this.setNodeLoading(nodeId, true);
    try {
      const fetchedSections = await fetchFn();

      // Check if we're fetching for a section that wants to load its own children
      // vs an item that wants to load nested sections
      if (targetNode && targetNode.type === BrowseNavItemType.SECTION) {
        // For sections loading their own children, if we got a single section,
        // use its children directly. If we got multiple sections, use them as-is.
        if (fetchedSections.length === 1 && fetchedSections[0]) {
          this.updateItemChildren(nodeId, fetchedSections[0].children || []);
        } else {
          // Multiple sections become children
          this.updateItemChildren(nodeId, fetchedSections);
        }
      } else if (targetNode && targetNode.type === BrowseNavItemType.ITEM) {
        const first = fetchedSections[0];
        if (fetchedSections.length === 1 && first && isBrowseNavSection(first) && !first.sectionTitle) {
          // For items that return a single section without a title,
          // unwrap the section and use its children directly
          this.updateItemChildren(nodeId, first.children || []);
        } else {
          // For items (categories/subcategories), add the fetched items/sections as children
          this.updateItemChildren(nodeId, fetchedSections);
        }
      } else if (targetNode) {
        // For categories/subcategories, add the fetched items/sections as children
        this.updateItemChildren(nodeId, fetchedSections);
      }
    } finally {
      this.setNodeLoading(nodeId, false);
    }
  },
  /**
   * Update an item's children in the navigation structure
   */
  updateItemChildren(itemId, children) {
    if (!self.navigationStructure) return;

    // Create a deep clone of the navigation structure to update it
    const updatedStructure = this.updateItemChildrenRecursive(self.navigationStructure, itemId, children);
    if (updatedStructure && updatedStructure.type === BrowseNavItemType.SECTION) {
      self.navigationStructure = updatedStructure;
    }
  },
  /**
   * Recursively find and update an item's children in the navigation structure
   */
  updateItemChildrenRecursive(node, targetId, newChildren) {
    // If this is the target node, update its children
    if (node.id === targetId) {
      return {
        ...node,
        children: newChildren
      };
    }

    // If this node has children, recursively search them
    if (node.children && node.children.length > 0) {
      const updatedChildren = node.children.map(child => {
        const updatedChild = this.updateItemChildrenRecursive(child, targetId, newChildren);
        return updatedChild || child;
      });

      // Check if any children were actually updated
      const hasChanges = updatedChildren.some((child, index) => child !== (node.children ? node.children[index] : undefined));
      if (hasChanges) {
        return {
          ...node,
          children: updatedChildren
        };
      }
    }
    return null; // No changes needed
  },
  /**
   * Set active item at a specific level and clear deeper levels
   */
  setActiveItemAtLevel(item, level) {
    // Set the active item at this level
    self.navigationState.set(level.toString(), item);

    // Clear any deeper levels (they become invalid when parent changes)
    const keysToDelete = [];
    self.navigationState.forEach((_, key) => {
      const levelNum = parseInt(String(key), 10);
      if (levelNum > level) {
        keysToDelete.push(String(key));
      }
    });
    keysToDelete.forEach(key => self.navigationState.delete(key));
  },
  /**
   * Clear all navigation state (e.g., on mouse leave)
   */
  clearNavigationState() {
    self.navigationState.clear();
  },
  /**
   * Handle item activation - sets navigation state and fetches children if needed
   * This is a generic method that can be triggered by various user interactions (mouse enter, click, etc.)
   */
  async handleItemActivated(item, level) {
    // Update navigation state
    this.setActiveItemAtLevel(item, level);

    // Fetch children if fetchChildren exists (fetchNodeChildren handles duplicate prevention)
    if (item.fetchChildren) {
      await this.fetchNodeChildren(item.id, item.fetchChildren);
    }
  },
  /**
   * Handle section loading - for sections that have fetchChildren
   * Always reads the latest section state from the store
   */
  async handleSectionLoad(sectionId) {
    // Get the latest version of this section from the store
    const latestSection = self.navigationStructure ? self.findNodeInStructure(self.navigationStructure, sectionId) : undefined;
    if (!latestSection) {
      return;
    }

    // Fetch children if fetchChildren exists (fetchNodeChildren handles duplicate prevention)
    const fetchChildren = latestSection.fetchChildren;
    if (fetchChildren) {
      await this.fetchNodeChildren(sectionId, fetchChildren);
    }
  }
}));
//# sourceMappingURL=header-menu-store.js.map